import React, { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { minutes, schoolApi, showTime } from '../../common/schoolApi';
import { STATE } from './constants';
import { StudentPaperDialog } from './StudentPaperDialog';

/**
 * The marks list (GET /assignment/results/5).
 *
 * The API has already settled the assignment before answering: attempts past
 * their deadline are closed and no-shows after the window are `missed`, so
 * what is shown here is current without a cron.
 *
 * The averages are over students who SAT it. A no-show is counted, not
 * averaged as a zero; an excused student is out of it altogether.
 *
 * Release and excuse are offered only to the teacher who set it (the API
 * refuses anyone else with 403 regardless).
 */

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ minWidth: 110 }}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="h6">{value ?? '—'}</Typography>
    </Box>
);

const csvCell = (v: any) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const ResultsTab = ({ assignment, onChanged }: { assignment: any; onChanged: () => void }) => {
    const notify = useNotify();
    const [data, setData] = useState<any>(null);
    const [section, setSection] = useState<number | ''>('');
    const [picked, setPicked] = useState<Record<number, boolean>>({});
    const [excusing, setExcusing] = useState(false);
    const [reason, setReason] = useState('');
    const [open, setOpen] = useState<number | null>(null);

    const load = useCallback(() => {
        schoolApi
            .get(`assignment/results/${assignment.id}`, { class_section_id: section })
            .then(r => setData(r.result))
            .catch(e => notify(e.message, { type: 'error' }));
    }, [assignment.id, section]);
    useEffect(load, [load]);

    if (!data) return <Typography>Loading…</Typography>;

    const mine = assignment.is_mine;
    const s = data.summary;
    const excusable = (st: any) => st.state === 'assigned' || st.state === 'missed';
    const pickedIds = Object.keys(picked).filter(k => picked[Number(k)]).map(Number);

    const release = async () => {
        if (!window.confirm('Release the results? Every student will see their marks, the answers and the solutions. This cannot be taken back.')) return;
        try {
            await schoolApi.post(`assignment/release/${assignment.id}`);
            notify('Results released.', { type: 'success' });
            onChanged();
            load();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };

    const excuse = async () => {
        try {
            const r = await schoolApi.post(`assignment/excuse/${assignment.id}`, { user_ids: pickedIds, reason });
            const refused = r.result.rejected || [];
            notify(
                `${r.result.excused.length} excused` + (refused.length ? `; ${refused.length} not: ${refused.map((x: any) => x.reason).join('; ')}` : '.'),
                { type: refused.length ? 'warning' : 'success' }
            );
            setExcusing(false);
            setReason('');
            setPicked({});
            onChanged();
            load();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };

    const unexcuse = async (userId: number) => {
        try {
            await schoolApi.post(`assignment/unexcuse/${assignment.id}`, { user_ids: [userId] });
            notify('Excuse removed.', { type: 'success' });
            onChanged();
            load();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };

    const downloadCsv = () => {
        const head = ['Roll', 'Name', 'Section', 'State', 'Score', 'Out of', 'Percent', 'Rank', 'Right', 'Wrong', 'Blank', 'Time (s)', 'Submitted', 'Excuse'];
        const rows = data.students.map((st: any) => [
            st.roll_number, st.full_name, st.section, STATE[st.state]?.label || st.state, st.score, st.total_marks, st.percentage, st.rank,
            st.correct, st.incorrect, st.unanswered, st.time_taken, st.submitted_at, st.excused_reason,
        ]);
        const csv = [head, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        a.download = `${assignment.title.replace(/[^\w]+/g, '_')}_results.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const bandMax = Math.max(1, ...Object.values<number>(s.bands));
    return (
        <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <Stat label="Sat it" value={`${s.sat} / ${s.students}`} />
                        <Stat label="Average" value={s.average} />
                        <Stat label="Average %" value={s.average_percentage === null ? null : `${s.average_percentage}%`} />
                        <Stat label="Highest" value={s.highest} />
                        <Stat label="Median" value={s.median} />
                        <Stat label="Lowest" value={s.lowest} />
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 56, ml: 2 }} title="Students by percentage">
                            {Object.entries<number>(s.bands).map(([band, n]) => (
                                <Box key={band} sx={{ textAlign: 'center', width: 44 }}>
                                    <Box sx={{ height: `${(n / bandMax) * 36}px`, bgcolor: 'primary.main', borderRadius: 0.5, minHeight: n ? 3 : 0 }} />
                                    <Typography variant="caption" sx={{ fontSize: 10 }}>
                                        {band}%<br />({n})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Averages are over students who sat the test. Missed: {s.counts.missed}. Excused: {s.counts.excused}. Writing now:{' '}
                        {s.counts.in_progress}.
                    </Typography>
                </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                {(assignment.sections || []).length > 1 && (
                    <TextField select size="small" label="Section" value={section} onChange={e => setSection(e.target.value as any)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All my sections</MenuItem>
                        {assignment.sections.map((sec: any) => (
                            <MenuItem key={sec.id} value={sec.id}>
                                {sec.label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <Box sx={{ flex: 1 }} />
                {mine && assignment.show_results_mode === 'manual' && !assignment.results_released_at && (
                    <Button variant="contained" onClick={release}>
                        Release results to students
                    </Button>
                )}
                {mine && (
                    <Button disabled={pickedIds.length === 0} onClick={() => setExcusing(true)}>
                        Excuse {pickedIds.length || ''} selected
                    </Button>
                )}
                <Button onClick={downloadCsv}>Download CSV</Button>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {mine && <TableCell padding="checkbox" />}
                            <TableCell>Roll</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Section</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell align="right">Score</TableCell>
                            <TableCell align="right">%</TableCell>
                            <TableCell align="right">Rank</TableCell>
                            <TableCell align="right">Right / Wrong / Blank</TableCell>
                            <TableCell align="right">Time</TableCell>
                            <TableCell>Submitted</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.students.map((st: any) => (
                            <TableRow
                                key={st.user_id}
                                hover
                                sx={{ cursor: st.attempts_used > 0 ? 'pointer' : 'default' }}
                                onClick={() => st.attempts_used > 0 && setOpen(st.user_id)}
                            >
                                {mine && (
                                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                        {excusable(st) && (
                                            <Checkbox
                                                size="small"
                                                checked={!!picked[st.user_id]}
                                                onChange={e => setPicked({ ...picked, [st.user_id]: e.target.checked })}
                                            />
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>{st.roll_number}</TableCell>
                                <TableCell>{st.full_name}</TableCell>
                                <TableCell>{st.section}</TableCell>
                                <TableCell onClick={e => st.state === 'excused' && e.stopPropagation()}>
                                    <Chip size="small" color={STATE[st.state]?.colour} label={STATE[st.state]?.label || st.state} />
                                    {st.state === 'excused' && (
                                        <>
                                            <Typography variant="caption" sx={{ ml: 1 }}>
                                                {st.excused_reason}
                                            </Typography>
                                            {mine && (
                                                <Button size="small" onClick={() => unexcuse(st.user_id)}>
                                                    Undo
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </TableCell>
                                <TableCell align="right">{st.score === null ? '—' : `${st.score} / ${st.total_marks}`}</TableCell>
                                <TableCell align="right">{st.percentage === null ? '—' : st.percentage}</TableCell>
                                <TableCell align="right">{st.rank ?? '—'}</TableCell>
                                <TableCell align="right">
                                    {st.correct === null ? '—' : `${st.correct} / ${st.incorrect} / ${st.unanswered}`}
                                </TableCell>
                                <TableCell align="right">{minutes(st.time_taken)}</TableCell>
                                <TableCell>{st.submitted_at ? showTime(st.submitted_at) : '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            {data.students.length === 0 && <Alert severity="info">No students in your sections were set this test.</Alert>}

            <Dialog open={excusing} onClose={() => setExcusing(false)} fullWidth maxWidth="sm">
                <DialogTitle>Excuse {pickedIds.length} student{pickedIds.length === 1 ? '' : 's'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        They will not be able to start this test, and are left out of the averages. The reason stays on the record.
                    </Typography>
                    <TextField autoFocus fullWidth label="Reason" value={reason} onChange={e => setReason(e.target.value)} inputProps={{ maxLength: 255 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExcusing(false)}>Back</Button>
                    <Button variant="contained" disabled={reason.trim() === ''} onClick={excuse}>
                        Excuse
                    </Button>
                </DialogActions>
            </Dialog>

            {open !== null && <StudentPaperDialog assignmentId={assignment.id} userId={open} onClose={() => setOpen(null)} />}
        </Box>
    );
};
